import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import {LOCAL_STORAGE_KEYS, PAGE_URLS, SIGNIN_TYPES} from "constants/AppConstants.js";
import LocalStorageService from "services/LocalStorageService.js";
import ToastService from "services/ToastService.js";
import {updateUserProfile} from "services/ProfileService";
import {refreshSubscriptionFromAdapty} from "services/SubscriptionService";
import {
  describePaymentLink,
  fetchPlanPrices,
  prefetchPlanPrices,
  startPaymentLinkCheckout,
  startTrialCheckout
} from "services/WebCheckoutService";
import {warmUpFunction} from "services/CloudFunctionWarmup";
import {openPaddleCheckout, preloadPaddle} from "services/PaddleCheckout";
import AuthService from "services/AuthService";
import type {CheckoutPlan, CheckoutTier, PaymentLinkOffer} from "services/WebCheckoutService";
import {getBlockingStoreSubscription} from "services/EntitlementService";
import {
  ADAPTY_ONBOARDING_PLACEMENT,
  ensureAdaptyProfile,
  fetchOnboardingPaywall,
  recordPaywallView
} from "services/AdaptyWebService";
import {
  initMetaPixel,
  rememberCheckout,
  trackCompleteRegistration,
  trackInitiateCheckout,
  trackOnboardingSelection,
  trackPurchase
} from "services/MetaPixelService";
import useSignIn from "hooks/useSignIn";
import {useAppUser} from "contexts/AppUserContext";
import {customLogEvent, customSetScreenView} from "FirebaseUtils";
import OnboardingHeader from "components/Onboarding/OnboardingHeader";
import OnboardingProgressBar from "components/Onboarding/OnboardingProgressBar";
import LandingStep from "components/Onboarding/LandingStep";
import AuthOptionsStep from "components/Onboarding/AuthOptionsStep";
import CreateAccountStep from "components/Onboarding/CreateAccountStep";
import type {AccountMode} from "components/Onboarding/CreateAccountStep";
import NewsletterStep from "components/Onboarding/NewsletterStep";
import PlanPickerCard from "components/Subscription/PlanPickerCard";
import ReadySetGoStep from "components/Onboarding/ReadySetGoStep";
import LegalLinks from "components/Common/LegalLinks";
import PaymentLinkOfferStep from "components/Onboarding/PaymentLinkOfferStep";
import {
  CardSubtitle,
  CardTitle,
  CenteredContainer,
  GradientPage,
  LandingContainer,
  OnboardingCard,
  WhitePage
} from "components/Onboarding/onboardingStyles";

type OnboardingStep = "landing" | "options" | "account" | "newsletter" | "trial" | "payment" | "ready";

/* Fill fractions from the Figma "Web sign in" flow frames (02 → 05); the final screen has no bar */
const STEP_PROGRESS: Record<OnboardingStep, number> = {
  landing: 0,
  options: 0.185,
  account: 0.54,
  newsletter: 0.8,
  trial: 1,
  payment: 1,
  ready: 1
};

function OnboardingPage() {
  const {t} = useTranslation();
  const history = useHistory();
  const {user, appUser} = useAppUser();
  const {handlerSignIn} = useSignIn();
  const [step, setStep] = useState<OnboardingStep>("landing");
  const [accountMode, setAccountMode] = useState<AccountMode>("signup");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paywallVariationId, setPaywallVariationId] = useState<string | undefined>();
  const paywallPrepared = useRef(false);
  // A payment link (/pay?link=…, redirected here) replaces the plan picker with its
  // offer: sign in or up, then pay for exactly what support offered.
  const [linkCode] = useState(() => new URLSearchParams(window.location.search).get("link"));
  const [offer, setOffer] = useState<PaymentLinkOffer | null | undefined>(linkCode ? undefined : null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const currentUid = () => user?.uid || LocalStorageService.get(LOCAL_STORAGE_KEYS.uid);

  useEffect(() => {
    if (linkCode) {
      describePaymentLink(linkCode).then(setOffer);
    }
  }, [linkCode]);

  useEffect(() => {
    (async () => {
      await customSetScreenView("OnboardingPage");
    })();
    initMetaPixel();

    // Returning from a paid checkout (or an older Stripe redirect's cancel)
    const checkoutResult = new URLSearchParams(window.location.search).get("checkout");
    if (checkoutResult === "success") {
      customLogEvent("Web Onboarding Trial Started", undefined);
      trackPurchase();
      refreshSubscriptionFromAdapty();
      setStep("ready");
      history.replace(PAGE_URLS.onboardingPageUrl);
    } else if (checkoutResult === "cancelled") {
      customLogEvent("Web Onboarding Trial Checkout Cancelled", undefined);
      setStep("trial");
      history.replace(PAGE_URLS.onboardingPageUrl);
    }

    // Paddle's default payment link (/pay?_ptxn=…, redirected here): Paddle.js opens
    // the checkout for that transaction by itself once it is initialized on the page.
    if (new URLSearchParams(window.location.search).has("_ptxn")) {
      preloadPaddle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step === "options") {
      trackOnboardingSelection();
    } else if (step === "trial" || step === "payment") {
      trackInitiateCheckout();
    }
  }, [step]);

  async function handleOAuth(signInType: string) {
    setIsSubmitting(true);
    const result = await handlerSignIn(signInType, {}, true, {stayOnPage: true});
    setIsSubmitting(false);
    if (result) {
      if (typeof result === "object" && result.isNewUser) {
        trackCompleteRegistration({
          email: user?.email,
          externalId: currentUid(),
          method: signInType.toLowerCase()
        });
      }
      setStep("newsletter");
    }
  }

  async function handleEmailSubmit(fields: {name: string; email: string; password: string}) {
    setIsSubmitting(true);
    const result = await handlerSignIn(SIGNIN_TYPES.emailAndPassword, fields, true, {stayOnPage: true});
    setIsSubmitting(false);
    if (result) {
      if (typeof result === "object" && result.isNewUser) {
        trackCompleteRegistration({email: fields.email, externalId: currentUid(), method: "email"});
      }
      setStep("newsletter");
    }
  }

  async function handleEmailLogin(fields: {email: string; password: string}) {
    setIsSubmitting(true);
    const result = await handlerSignIn(SIGNIN_TYPES.emailAndPassword, fields, false, {stayOnPage: true});
    setIsSubmitting(false);
    if (result) {
      customLogEvent("Web Onboarding Login", {link: !!linkCode});
      // An existing account was already asked about the newsletter when it signed up.
      setStep(linkCode ? "payment" : "newsletter");
    }
  }

  function showLogin() {
    setAccountMode("login");
    setStep("account");
  }

  // Prepared a step early: these are rarely-used Cloud Functions, and their cold
  // start is hidden while the user answers the newsletter question.
  useEffect(() => {
    if (step !== "newsletter" && step !== "trial" && step !== "payment") {
      return;
    }
    const uid = currentUid();
    if (!uid || paywallPrepared.current) {
      return;
    }
    paywallPrepared.current = true;
    if (linkCode) {
      // No plan picker and no Adapty paywall impression: the link's offer is paid as is.
      warmUpFunction("startPaddlePaymentLink");
      preloadPaddle();
      // The purchase attaches to this profile, so it should carry their email and name.
      ensureAdaptyProfile();
      return;
    }
    prefetchPlanPrices(ADAPTY_ONBOARDING_PLACEMENT);
    warmUpFunction("createPaddleCheckout");
    preloadPaddle();
    (async () => {
      // Web-first users have no Adapty profile yet; create it so getPaywall resolves
      await ensureAdaptyProfile();
      const paywall = await fetchOnboardingPaywall(uid);
      if (paywall) {
        setPaywallVariationId(paywall.variationId);
        recordPaywallView(paywall.variationId, uid);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  async function handleNewsletterAnswer(consent: boolean) {
    customLogEvent("Web Onboarding Newsletter", {consent});
    // Best-effort — a failed marketing write must not block the wizard
    await updateUserProfile({marketing: consent});
    setStep(linkCode ? "payment" : "trial");
  }

  async function handlePayLink() {
    if (!linkCode) {
      return;
    }
    customLogEvent("Web Onboarding Payment Link CTA", undefined);
    setIsSubmitting(true);
    setPaymentError(null);
    const checkout = await startPaymentLinkCheckout(linkCode);
    if (!checkout.transactionId) {
      setIsSubmitting(false);
      const reasonTexts: Record<string, string> = {
        used: t("payLink.usedText"),
        expired: t("payLink.expiredText")
      };
      setPaymentError(reasonTexts[checkout.reason ?? ""] ?? t("payLink.failedText"));
      return;
    }
    const outcome = await openPaddleCheckout(checkout.transactionId).catch(() => "closed" as const);
    if (outcome !== "completed") {
      setIsSubmitting(false);
      return;
    }
    customLogEvent("Web Onboarding Payment Link Paid", undefined);
    setIsSubmitting(false);
    setStep("ready");
    // Paddle tells Adapty a few seconds after the checkout closes.
    for (let attempt = 0; attempt < 6; attempt++) {
      const result = await refreshSubscriptionFromAdapty();
      if (result?.subscription?.premium?.expiration || result?.subscription?.ultra?.expiration) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
  }

  // An account already signed in takes the offer straight away; another one can
  // be used instead, which signs this one out first.
  function handleOfferContinue() {
    setStep(user ? "payment" : "options");
  }

  async function handleUseAnotherAccount() {
    await AuthService.signOutAsync(false);
    LocalStorageService.clear();
    setStep("landing");
  }

  async function handleStartTrial(tier: CheckoutTier, plan: CheckoutPlan) {
    customLogEvent("Web Onboarding Trial CTA", {plan, tier});

    // The wizard doubles as a login, so an existing subscriber reaches this step and
    // could be billed twice. createPaddleCheckout enforces this authoritatively;
    // here it just saves a round trip.
    const blocking = getBlockingStoreSubscription(appUser);
    if (blocking) {
      customLogEvent("Web Onboarding Trial Blocked", {store: blocking.store});
      ToastService.error(t("subscribe.manageInStore"), "top-right");
      return;
    }

    const prices = await fetchPlanPrices(ADAPTY_ONBOARDING_PLACEMENT);
    const price = prices?.[tier]?.[plan];
    rememberCheckout({
      value: price ? price.amount / 100 : 0,
      currency: price?.currency?.toUpperCase() || "EUR",
      contentName: `${tier}_${plan}`
    });

    setIsSubmitting(true);
    const result = await startTrialCheckout(plan, {
      tier,
      variationId: paywallVariationId,
      placementId: ADAPTY_ONBOARDING_PLACEMENT
    });
    if (result.cancelled) {
      setIsSubmitting(false);
      customLogEvent("Web Onboarding Trial Checkout Cancelled", undefined);
    } else if (!result.success) {
      setIsSubmitting(false);
      ToastService.error(t("onboarding.checkoutError"), "top-right");
    }
    // On success the browser goes to `?checkout=success`, handled on load.
  }

  function handleSkipTrial() {
    customLogEvent("Web Onboarding Trial Skipped", undefined);
    setStep("ready");
  }

  function handleGoToWebapp() {
    customLogEvent("Web Onboarding Completed", undefined);
    history.push(PAGE_URLS.diaryPageUrl);
  }

  if (step === "landing" && linkCode) {
    return (
      <WhitePage>
        <OnboardingHeader onLogin={showLogin} />
        <GradientPage as="main" style={{minHeight: "auto", flex: 1}}>
          <CenteredContainer>
            {offer === null ? (
              <OnboardingCard>
                <CardTitle>{t("payLink.invalidTitle")}</CardTitle>
                <CardSubtitle>{t("payLink.invalidText")}</CardSubtitle>
              </OnboardingCard>
            ) : (
              offer && (
                <PaymentLinkOfferStep
                  offer={offer}
                  mode="intro"
                  signedInEmail={user?.email}
                  error={offer.expired ? t("payLink.expiredText") : offer.used ? t("payLink.usedText") : null}
                  onContinue={handleOfferContinue}
                  onUseAnotherAccount={handleUseAnotherAccount}
                />
              )
            )}
          </CenteredContainer>
        </GradientPage>
        <LegalLinks />
      </WhitePage>
    );
  }

  if (step === "landing") {
    return (
      <WhitePage>
        <OnboardingHeader showCreateAccount onCreateAccount={() => setStep("options")} />
        <LandingContainer>
          <LandingStep onGetStarted={() => setStep("options")} />
        </LandingContainer>
        <LegalLinks />
      </WhitePage>
    );
  }

  return (
    <WhitePage>
      <OnboardingHeader onLogin={linkCode ? showLogin : undefined} />
      <GradientPage as="main" style={{minHeight: "auto", flex: 1}}>
        <CenteredContainer>
          {step !== "ready" && <OnboardingProgressBar fraction={STEP_PROGRESS[step]} />}
          {step === "options" && (
            <AuthOptionsStep
              onApple={() => handleOAuth(SIGNIN_TYPES.apple)}
              onGoogle={() => handleOAuth(SIGNIN_TYPES.google)}
              onEmail={() => {
                setAccountMode("signup");
                setStep("account");
              }}
              onLogin={showLogin}
            />
          )}
          {step === "account" && (
            <CreateAccountStep
              key={accountMode}
              initialMode={accountMode}
              onSubmit={handleEmailSubmit}
              onLogin={handleEmailLogin}
              isSubmitting={isSubmitting}
            />
          )}
          {step === "newsletter" && <NewsletterStep onAnswer={handleNewsletterAnswer} />}
          {step === "trial" && (
            <PlanPickerCard
              tiers={["premium", "ultra"]}
              trialEligible={true}
              isSubmitting={isSubmitting}
              onSubscribe={handleStartTrial}
              onCancel={handleSkipTrial}
              cancelLabel={t("onboarding.noThanks")}
              placementId={ADAPTY_ONBOARDING_PLACEMENT}
            />
          )}
          {step === "payment" && offer && (
            <PaymentLinkOfferStep
              offer={offer}
              mode="pay"
              isSubmitting={isSubmitting}
              error={paymentError}
              onContinue={handlePayLink}
            />
          )}
          {step === "ready" && <ReadySetGoStep onGoToWebapp={handleGoToWebapp} />}
        </CenteredContainer>
      </GradientPage>
      <LegalLinks />
    </WhitePage>
  );
}

export default OnboardingPage;
